export const PAMI_TRAIT_PREFIXES = [
  "Statut",
  "Manag",
  "Equi",
  "Secu",
  "Auto",
  "Rea",
  "Var",
  "Rev",
  "H1",
  "H2",
  "N1",
  "N2",
  "E1",
  "E2",
  "A1",
  "A2",
  "C1",
  "C2",
  "O1",
  "O2",
];

export const PAMI_HEXACO_FACTORS = [
  {
    key: "H",
    label: "Honnêteté-Humilité",
    facets: [
      { prefix: "H1", code: "H1", label: "Sincérité" },
      { prefix: "H2", code: "H2", label: "Intégrité" },
    ],
  },
  {
    key: "E",
    label: "Émotivité",
    facets: [
      { prefix: "N1", code: "E1", label: "Calme" },
      { prefix: "N2", code: "E2", label: "Gestion du stress" },
    ],
  },
  {
    key: "X",
    label: "eXtraversion",
    facets: [
      { prefix: "E1", code: "X1", label: "Affirmation de soi" },
      { prefix: "E2", code: "X2", label: "Sociabilité" },
    ],
  },
  {
    key: "A",
    label: "Agréabilité",
    facets: [
      { prefix: "A1", code: "A1", label: "Gestion des conflits" },
      { prefix: "A2", code: "A2", label: "Altruisme" },
    ],
  },
  {
    key: "C",
    label: "Conscienciosité",
    facets: [
      { prefix: "C1", code: "C1", label: "Autodiscipline" },
      { prefix: "C2", code: "C2", label: "Professionnalisme" },
    ],
  },
  {
    key: "O",
    label: "Ouverture",
    facets: [
      { prefix: "O1", code: "O1", label: "Intellect" },
      { prefix: "O2", code: "O2", label: "Créativité" },
    ],
  },
];

export const PAMI_MOTIVATION_DIMS = [
  { prefix: "Auto", label: "Autonomie" },
  { prefix: "Equi", label: "Équilibre vie pro/perso" },
  { prefix: "Manag", label: "Qualité du management" },
  { prefix: "Rea", label: "Réalisation de soi" },
  { prefix: "Rev", label: "Revenus" },
  { prefix: "Secu", label: "Sécurité professionnelle" },
  { prefix: "Statut", label: "Statut social" },
  { prefix: "Var", label: "Variété des tâches" },
];

/**
 * Les 20 dimensions d'un référentiel PAMI : 8 motivations + 12 facettes HEXACO
 * (plan référentiel §6.3, décision 1 — pas de sous-ensemble, pas de réglage).
 *
 * `code` est le PRÉFIXE de trait, c'est-à-dire exactement la clé renvoyée par
 * `computePamiScores` : le matching candidat ↔ référentiel se fait sans table de
 * correspondance. (Attention : pour les facettes HEXACO, `prefix` et `code`
 * diffèrent dans `PAMI_HEXACO_FACTORS` — c'est bien `prefix` qui porte le score.)
 *
 * `categorie` sépare les deux blocs de saisie : un référent qui enchaîne 20
 * curseurs identiques ne distingue pas ce qu'il est en train de faire.
 */
export const PAMI_REFERENTIEL_DIMENSIONS: {
  code: string;
  label: string;
  description?: string;
  categorie: "motivation" | "personnalite";
  origine: "pami";
}[] = [
  ...PAMI_MOTIVATION_DIMS.map((dim) => ({
    code: dim.prefix,
    label: dim.label,
    categorie: "motivation" as const,
    origine: "pami" as const,
  })),
  ...PAMI_HEXACO_FACTORS.flatMap((factor) =>
    factor.facets.map((facet) => ({
      code: facet.prefix,
      label: facet.label,
      description: `Facette de ${factor.label}`,
      categorie: "personnalite" as const,
      origine: "pami" as const,
    })),
  ),
];

export function computePamiScores(
  reponses: Record<string, { orderedItems: { id: string }[] }>,
): Record<string, number> {
  const rawScores: Record<string, number> = {};
  const maxScores: Record<string, number> = {};

  Object.values(reponses).forEach((reponse) => {
    reponse.orderedItems.forEach((item, position) => {
      const prefix = PAMI_TRAIT_PREFIXES.find((p) => item.id.startsWith(p));
      if (!prefix) return;
      rawScores[prefix] = (rawScores[prefix] || 0) + (2 - position);
      maxScores[prefix] = (maxScores[prefix] || 0) + 2;
    });
  });

  const scores: Record<string, number> = {};
  PAMI_TRAIT_PREFIXES.forEach((prefix) => {
    const max = maxScores[prefix] || 1;
    scores[prefix] = Math.round(((rawScores[prefix] || 0) / max) * 10);
  });

  return scores;
}
